import { ReactNode, useMemo } from 'react'
import { Button } from '~/components/common/Button'
import { useConversationUuid } from '~/hooks/conversation/useConversationUuid'
import { useTaskDetails } from '~/hooks/task/useTaskDetails'
import { ModalId, useModalController } from '~/hooks/useModal'
import { AgentTaskStatus, Diff } from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { TaskObject } from '~/types/TaskType'
import { SLIDE_CONFIGS } from './slideConfig'

interface MuleRefactorFlowLayoutProps {
  children: ReactNode
  /** Current timeline step (0 = repo selection, 1..5 = in-task slides, 6 = error). */
  currentStepNumber: number
  /** Required only for in-task screens; omit when rendering the repo-selection chrome. */
  taskId?: number
  proceedButtonText?: string
  onProceed?: () => void
  isProceedDisabled?: boolean
  /** Shows a spinner and locks the proceed button while a step submission is in flight. */
  isProceedLoading?: boolean
}

/**
 * Returns diffs for the most recent occurrence of any status in `targetStatuses`,
 * checked in priority order. Returns the first non-empty match.
 */
function getDiffsForStatuses(
  task: TaskObject | undefined,
  targetStatuses: AgentTaskStatus[]
): Diff[] {
  const tuples = task?.details?.orderedStateResponses
  if (!tuples) return []

  for (const targetStatus of targetStatuses) {
    for (let i = tuples.length - 1; i >= 0; i--) {
      const tuple = tuples[i]
      if (tuple.taskStatus !== targetStatus) continue
      const diffs = tuple.response?.[0]?.responseContentStream?.diff ?? []
      if (diffs.length > 0) return diffs
      break
    }
  }
  return []
}

/** Step number → diff statuses for the timeline file viewer button. */
const TIMELINE_DIFF_STATUSES: Record<number, AgentTaskStatus[]> = {
  2: [AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED],
  4: [AgentTaskStatus.REFACTOR_API_PROPOSED, AgentTaskStatus.REFACTOR_API_PROPOSING],
  5: [AgentTaskStatus.REFACTOR_CODE_GENERATED, AgentTaskStatus.REFACTOR_API_SPECS_GENERATED]
}

export function MuleRefactorFlowLayout({
  children,
  currentStepNumber,
  taskId,
  proceedButtonText = 'Proceed to Code',
  onProceed,
  isProceedDisabled = true,
  isProceedLoading = false
}: MuleRefactorFlowLayoutProps) {
  const isPlugin = bridge.isInPlugin()
  const conversationUuid = useConversationUuid()
  const { openModal } = useModalController(ModalId.RefactorFileViewer)
  const { data: taskDetailsData } = useTaskDetails(conversationUuid, taskId?.toString() ?? '')
  const task = taskDetailsData?.task

  // Memoize diffs per timeline step so the file viewer reflects task progress.
  const diffsByStep = useMemo<Record<number, Diff[]>>(() => {
    const result: Record<number, Diff[]> = {}
    for (const [stepStr, statuses] of Object.entries(TIMELINE_DIFF_STATUSES)) {
      result[Number(stepStr)] = getDiffsForStatuses(task, statuses)
    }
    // Step 4 (Refactored Docs) excludes dependency graph PNGs, which belong to the inline slide.
    result[4] = result[4].filter(diff => !diff.bFilePath?.endsWith('.png'))
    return result
  }, [task])

  const handleTimelineItemClick = (stepNumber: number) => {
    const diffs = diffsByStep[stepNumber] ?? []
    if (diffs.length > 0) openModal({ diffs })
  }

  // The proceed button is hidden on the final/terminal slides.
  const hideButton = currentStepNumber >= 5

  return (
    <div className='flex flex-col w-full h-full'>
      {/* Nav Header */}
      <div
        className={`bg-white border-b border-solid border-[#eaeaea] flex items-center justify-end ${isPlugin ? 'px-3 h-[48px]' : 'px-[32px] h-[64px]'} flex-shrink-0`}
      >
        {!hideButton && (
          <Button
            variant='primary'
            onClick={onProceed}
            isDisabled={isProceedDisabled}
            isLoading={isProceedLoading}
            className="font-['Geist'] text-[14px]"
            rightIcon={
              isProceedLoading ? undefined : (
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='shrink-0'
                >
                  <path
                    d='M6 4l4 4-4 4'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              )
            }
          >
            {proceedButtonText}
          </Button>
        )}
      </div>

      {/* Main Content - Timeline + Children */}
      <div className='flex w-full h-full overflow-hidden'>
        {!isPlugin && (
          <div className='w-[313px] flex-shrink-0 flex flex-col px-[48px] pt-[24px] pb-[60px] rounded-[24px] border-r border-solid border-[#eaeaea]'>
            <div className='h-[24px] mb-[16px]' />
            <div className='pt-[32px] pb-[24px]'>
              <p className="font-['Geist'] font-bold text-[14px] leading-[18px] text-[rgba(0,0,0,0.45)]">
                REFACTOR WORKFLOW
              </p>
            </div>
            <div className='flex flex-col gap-[16px]'>
              {SLIDE_CONFIGS.map((step, index) => (
                <TimelineStep
                  key={step.id}
                  stepNumber={step.stepNumber}
                  title={step.title}
                  subtitle={step.subtitle}
                  currentStepNumber={currentStepNumber}
                  hasArtifacts={(diffsByStep[step.stepNumber] ?? []).length > 0}
                  isLastInList={index === SLIDE_CONFIGS.length - 1}
                  onClick={() => handleTimelineItemClick(step.stepNumber)}
                />
              ))}
            </div>
          </div>
        )}
        <div className='flex-1 overflow-auto'>{children}</div>
      </div>
    </div>
  )
}

interface TimelineStepProps {
  stepNumber: number
  title: string
  subtitle?: string
  currentStepNumber: number
  hasArtifacts: boolean
  isLastInList: boolean
  onClick: () => void
}

function TimelineStep({
  stepNumber,
  title,
  subtitle,
  currentStepNumber,
  hasArtifacts,
  isLastInList,
  onClick
}: TimelineStepProps) {
  const isActive = currentStepNumber === stepNumber
  const isPassed = currentStepNumber > stepNumber
  const isFuture = currentStepNumber < stepNumber
  const isClickable = (isActive || isPassed) && hasArtifacts

  const titleColor = isActive
    ? 'text-[#5B5BD5]'
    : isFuture
      ? 'text-[rgba(0,0,0,0.25)]'
      : 'text-[rgba(0,0,0,0.88)]'

  return (
    <div className={`flex gap-[16px] items-start w-full ${!subtitle ? 'h-[60px]' : ''}`}>
      <div
        className={`flex flex-col gap-[6px] items-center pb-[6px] pt-[7px] ${!subtitle ? 'h-full' : 'self-stretch'}`}
      >
        <div className={`relative shrink-0 ${isActive ? 'w-[10px] h-[10px]' : 'w-[8px] h-[8px]'}`}>
          <div
            className={`absolute inset-0 rounded-full ${
              isPassed || isActive ? 'bg-[#9D7EE6]' : 'bg-[rgba(0,0,0,0.15)]'
            }`}
          />
        </div>
        {!isLastInList && (
          <div className='flex-1 min-h-px min-w-px relative shrink-0 w-0'>
            <div className='absolute bottom-0 left-0 right-full top-0'>
              <div
                className={`absolute bottom-0 top-0 ${
                  isPassed ? 'bg-[#9D7EE6]' : 'bg-[rgba(0,0,0,0.15)]'
                }`}
                style={{ left: '-0.5px', right: '-0.5px' }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-[8px] items-start pb-[12px] pt-0 px-0 font-['Geist']">
        <p className={`leading-[20px] text-[16px] ${titleColor} font-medium`}>{title}</p>
        {subtitle && (
          <TimelineSubtitleButton
            label={subtitle}
            isActive={isActive}
            isFuture={isFuture}
            isClickable={isClickable}
            onClick={onClick}
          />
        )}
      </div>
    </div>
  )
}

interface TimelineSubtitleButtonProps {
  label: string
  isActive: boolean
  isFuture: boolean
  isClickable: boolean
  onClick: () => void
}

function TimelineSubtitleButton({
  label,
  isActive,
  isFuture,
  isClickable,
  onClick
}: TimelineSubtitleButtonProps) {
  if (isFuture) {
    return (
      <p className="font-['Geist'] font-normal text-[14px] leading-[16px] text-[rgba(0,0,0,0.25)] tracking-[0.56px]">
        {label}
      </p>
    )
  }

  const iconStroke = isActive ? '#5B5BD5' : 'rgba(142,142,142,1)'
  const iconHoverClass =
    isClickable && !isActive ? 'group-hover:!stroke-[#5B5BD5] transition-colors' : ''

  return (
    <div
      className={`flex flex-col h-[36px] items-center justify-center overflow-clip rounded-[12px] transition-colors ${
        isClickable ? 'cursor-pointer' : ''
      } ${isClickable && !isActive ? 'group' : ''} ${isActive ? 'bg-[rgba(132,111,225,0.1)]' : ''}`}
      onClick={isClickable ? onClick : undefined}
    >
      <div className='flex gap-[8px] items-center justify-center px-[16px] py-[8px] w-full h-full'>
        <svg
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          className='shrink-0'
        >
          <path
            d='M9.33268 1.33337H3.99935C3.64573 1.33337 3.30659 1.47385 3.05654 1.7239C2.80649 1.97395 2.66602 2.31309 2.66602 2.66671V13.3334C2.66602 13.687 2.80649 14.0261 3.05654 14.2762C3.30659 14.5262 3.64573 14.6667 3.99935 14.6667H11.9993C12.353 14.6667 12.6921 14.5262 12.9422 14.2762C13.1922 14.0261 13.3327 13.687 13.3327 13.3334V5.33337L9.33268 1.33337Z'
            stroke={iconStroke}
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            className={iconHoverClass}
          />
          <path
            d='M9.33398 1.33337V5.33337H13.334'
            stroke={iconStroke}
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            className={iconHoverClass}
          />
          <path
            d='M10.6673 8.66663H5.33398'
            stroke={iconStroke}
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            className={iconHoverClass}
          />
          <path
            d='M10.6673 11.3334H5.33398'
            stroke={iconStroke}
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            className={iconHoverClass}
          />
          <path
            d='M6.66732 6H6.00065H5.33398'
            stroke={iconStroke}
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            className={iconHoverClass}
          />
        </svg>
        <span
          className={`font-['Geist'] font-medium text-[14px] leading-[22px] text-center whitespace-nowrap ${
            isActive
              ? 'text-[#5B5BD5]'
              : isClickable
                ? 'text-[rgba(0,0,0,0.45)] group-hover:text-[#5B5BD5] transition-colors'
                : 'text-[rgba(0,0,0,0.45)]'
          }`}
        >
          {label}
        </span>
      </div>
    </div>
  )
}
